import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { useLanguageStore } from '../../stores/languageStore';
import { RefreshCw, Database, Heart, Users, Check, X, ArrowLeftRight } from 'lucide-react';
import toast from 'react-hot-toast';

interface BaseOrder {
  id: number;
  created_at: string;
  order_type?: 'expansion' | 'donation' | 'room_membership';
  type?: 'expansion' | 'donation' | 'room_membership';
}

interface ExpansionOrder extends BaseOrder {
  user_id: number;
  username: string;
  space_added: number;
  space_added_desc: string;
  amount: number;
  actual_amount?: number;
  system_note: string;
  txid?: string;
  sender_address?: string;
  order_status: 'pending' | 'awaiting_confirmation' | 'auto_success' | 'manual_success' | 'expired' | 'completed' | 'refunded';
  expired_at: string;
  order_type: 'expansion';
}

interface DonationOrder extends BaseOrder {
  user_id: number;
  username: string;
  amount: number;
  is_anonymous: boolean;
  system_note: string;
  txid?: string;
  sender_address?: string;
  status: 'pending' | 'awaiting_confirmation' | 'success' | 'completed' | 'refunded';
  order_type: 'donation';
}

interface RoomMembershipOrder extends BaseOrder {
  user_id: number;
  username: string;
  room_id: number;
  room_name: string;
  amount: number;
  price_type: string;
  system_note: string;
  txid?: string;
  sender_address?: string;
  status: 'pending' | 'awaiting_confirmation' | 'success' | 'completed' | 'expired';
  order_type: 'room_membership';
}

interface RoomPackageOrder extends BaseOrder {
  user_id: number;
  username: string;
  amount: number;
  period: 'monthly' | 'quarterly' | 'yearly';
  system_note: string;
  txid?: string;
  sender_address?: string;
  status: 'pending' | 'awaiting_confirmation' | 'success' | 'completed' | 'expired';
  order_type: 'room_package';
}

interface RoomCreationOrder extends BaseOrder {
  user_id: number;
  username: string;
  room_name: string;
  room_description?: string;
  amount: number;
  system_note: string;
  txid?: string;
  sender_address?: string;
  status: 'pending' | 'awaiting_confirmation' | 'success' | 'completed' | 'expired';
  order_type: 'room_creation';
}

type Order = ExpansionOrder | DonationOrder | RoomMembershipOrder | RoomPackageOrder | RoomCreationOrder;

const AdminPendingOrders: React.FC = () => {
  const { language } = useLanguageStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState<'complete' | 'refund' | 'cancel' | 'upgrade' | null>(null);
  const [selectedSpaceAmount, setSelectedSpaceAmount] = useState<number>(0);
  const [searchUsername, setSearchUsername] = useState('');
  const pageSize = 20;

  useEffect(() => {
    loadOrders();
  }, [page, searchUsername]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      let url = `/payment/admin/all-pending-orders?page=${page}&pageSize=${pageSize}`;
      if (searchUsername.trim()) {
        url += `&username=${encodeURIComponent(searchUsername.trim())}`;
      }
      const response = await api.get<{ success: boolean; orders: any[]; pagination: { total: number } }>(url);

      if (response.success) {
        setOrders(response.orders);
        setTotal(response.pagination.total);
      }
    } catch (error) {
      console.error('加载待处理订单失败:', error);
      toast.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (address: string | null | undefined) => {
    if (!address) return '-';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatTime = (time: string | null | undefined) => {
    if (!time) return '-';
    return new Date(time).toLocaleString('zh-CN');
  };

  const isExpansionOrder = (order: Order): order is ExpansionOrder => {
    const o = order as any;
    return o.order_type === 'expansion' || o.type === 'expansion' || 'space_added' in order;
  };

  const isDonationOrder = (order: Order): order is DonationOrder => {
    const o = order as any;
    return o.order_type === 'donation' || o.type === 'donation' || 'is_anonymous' in order;
  };

  const isRoomMembershipOrder = (order: Order): order is RoomMembershipOrder => {
    const o = order as any;
    return o.order_type === 'room_membership' || o.type === 'room_membership' || 'room_id' in order;
  };

  const isRoomPackageOrder = (order: Order): order is RoomPackageOrder => {
    const o = order as any;
    return o.order_type === 'room_package' || o.type === 'room_package' || 'period' in order;
  };

  const isRoomCreationOrder = (order: Order): order is RoomCreationOrder => {
    const o = order as any;
    return o.order_type === 'room_creation' || o.type === 'room_creation' || 'room_name' in order;
  };

  const getStatusText = (order: Order) => {
    if (isExpansionOrder(order)) {
      const statusMap: Record<string, string> = {
        pending: '待支付',
        awaiting_confirmation: '等待确认',
        auto_success: '已完成',
        manual_success: '已完成',
        expired: '已过期',
        refunded: '已退款',
        cancelled: '已取消'
      };
      return statusMap[order.order_status] || order.order_status;
    }
    if (isDonationOrder(order)) {
      const statusMap: Record<string, string> = {
        pending: '待支付',
        awaiting_confirmation: '等待确认',
        success: '已完成',
        refunded: '已退款',
        cancelled: '已取消'
      };
      return statusMap[order.status] || order.status;
    }
    if (isRoomMembershipOrder(order)) {
      const statusMap: Record<string, string> = {
        pending: '待支付',
        awaiting_confirmation: '等待确认',
        success: '已完成',
        completed: '已完成',
        expired: '已过期',
        cancelled: '已取消'
      };
      return statusMap[order.status] || order.status;
    }
    if (isRoomPackageOrder(order)) {
      const statusMap: Record<string, string> = {
        pending: '待支付',
        awaiting_confirmation: '等待确认',
        success: '已完成',
        completed: '已完成',
        expired: '已过期',
        cancelled: '已取消'
      };
      return statusMap[order.status] || order.status;
    }
    if (isRoomCreationOrder(order)) {
      const statusMap: Record<string, string> = {
        pending: '待支付',
        awaiting_confirmation: '等待确认',
        success: '已完成',
        completed: '已完成',
        expired: '已过期',
        cancelled: '已取消'
      };
      return statusMap[order.status] || order.status;
    }
    return '';
  };

  const getStatusColor = (order: Order) => {
    if (isExpansionOrder(order)) {
      const colorMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        awaiting_confirmation: 'bg-blue-100 text-blue-800',
        auto_success: 'bg-green-100 text-green-800',
        manual_success: 'bg-green-100 text-green-800',
        expired: 'bg-gray-100 text-gray-800',
        refunded: 'bg-red-100 text-red-800',
        cancelled: 'bg-gray-100 text-gray-800'
      };
      return colorMap[order.order_status] || 'bg-gray-100 text-gray-800';
    }
    if (isDonationOrder(order)) {
      const colorMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        awaiting_confirmation: 'bg-blue-100 text-blue-800',
        success: 'bg-green-100 text-green-800',
        refunded: 'bg-red-100 text-red-800',
        cancelled: 'bg-gray-100 text-gray-800'
      };
      return colorMap[order.status] || 'bg-gray-100 text-gray-800';
    }
    if (isRoomMembershipOrder(order)) {
      const colorMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        awaiting_confirmation: 'bg-blue-100 text-blue-800',
        success: 'bg-green-100 text-green-800',
        completed: 'bg-green-100 text-green-800',
        expired: 'bg-gray-100 text-gray-800',
        cancelled: 'bg-gray-100 text-gray-800'
      };
      return colorMap[order.status] || 'bg-gray-100 text-gray-800';
    }
    if (isRoomPackageOrder(order)) {
      const colorMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        awaiting_confirmation: 'bg-blue-100 text-blue-800',
        success: 'bg-green-100 text-green-800',
        completed: 'bg-green-100 text-green-800',
        expired: 'bg-gray-100 text-gray-800',
        cancelled: 'bg-gray-100 text-gray-800'
      };
      return colorMap[order.status] || 'bg-gray-100 text-gray-800';
    }
    if (isRoomCreationOrder(order)) {
      const colorMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        awaiting_confirmation: 'bg-blue-100 text-blue-800',
        success: 'bg-green-100 text-green-800',
        completed: 'bg-green-100 text-green-800',
        expired: 'bg-gray-100 text-gray-800',
        cancelled: 'bg-gray-100 text-gray-800'
      };
      return colorMap[order.status] || 'bg-gray-100 text-gray-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const getOrderTypeIcon = (order: Order) => {
    if (isExpansionOrder(order)) {
      return <Database className="w-4 h-4 text-blue-500" />;
    }
    if (isDonationOrder(order)) {
      return <Heart className="w-4 h-4 text-pink-500" />;
    }
    if (isRoomMembershipOrder(order)) {
      return <Users className="w-4 h-4 text-purple-500" />;
    }
    if (isRoomPackageOrder(order)) {
      return <Users className="w-4 h-4 text-orange-500" />;
    }
    if (isRoomCreationOrder(order)) {
      return <Users className="w-4 h-4 text-indigo-500" />;
    }
    return null;
  };

  const getOrderTypeName = (order: Order) => {
    if (isExpansionOrder(order)) {
      return '扩容订单';
    }
    if (isDonationOrder(order)) {
      return '捐助订单';
    }
    if (isRoomMembershipOrder(order)) {
      return '房间会员订单';
    }
    if (isRoomPackageOrder(order)) {
      return '房主开通订单';
    }
    if (isRoomCreationOrder(order)) {
      return '创建房间订单';
    }
    return '';
  };

  const isOrderCompleted = (order: Order) => {
    if (isExpansionOrder(order)) {
      // 已取消的订单(cancelled)无法操作，视为已完成
      return order.order_status === 'auto_success' || 
             order.order_status === 'manual_success' || 
             order.order_status === 'refunded' ||
             order.order_status === 'cancelled';
    }
    if (isDonationOrder(order)) {
      // 已取消的订单(cancelled)无法操作，视为已完成
      return order.status === 'success' || order.status === 'refunded' || order.status === 'cancelled';
    }
    if (isRoomMembershipOrder(order)) {
      // 已取消的订单(cancelled)无法操作，视为已完成
      return order.status === 'success' || order.status === 'completed' || order.status === 'cancelled';
    }
    if (isRoomPackageOrder(order)) {
      // 已取消的订单(cancelled)无法操作，视为已完成
      return order.status === 'success' || order.status === 'completed' || order.status === 'cancelled';
    }
    if (isRoomCreationOrder(order)) {
      // 已取消的订单(cancelled)无法操作，视为已完成
      return order.status === 'success' || order.status === 'completed' || order.status === 'cancelled';
    }
    return true;
  };

  const handleAction = (order: Order, action: 'complete' | 'refund' | 'cancel' | 'upgrade') => {
    setSelectedOrder(order);
    setActionType(action);
    if (action === 'upgrade') {
      setSelectedSpaceAmount(0);
    }
    setShowModal(true);
  };

  const submitAction = async () => {
    if (!selectedOrder || !actionType) return;

    try {
      const params: any = {
        orderId: selectedOrder.id,
        actionType: actionType
      };

      if ((actionType === 'complete' || actionType === 'upgrade') && selectedSpaceAmount > 0) {
        params.spaceAmount = selectedSpaceAmount;
      }

      await api.post('/reconciliation/admin/process-order', params);
      toast.success('操作成功');
      setShowModal(false);
      loadOrders();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const getTimeLeft = (order: Order) => {
    // 只对pending状态的订单显示倒计时
    const isPending = (isExpansionOrder(order) && order.order_status === 'pending') ||
                      (isDonationOrder(order) && order.status === 'pending') ||
                      (isRoomMembershipOrder(order) && order.status === 'pending') ||
                      (isRoomPackageOrder(order) && order.status === 'pending') ||
                      (isRoomCreationOrder(order) && order.status === 'pending');
    
    if (!isPending) {
      return <span className="text-gray-400">-</span>;
    }
    
    const orderTime = new Date(order.created_at).getTime();
    const now = new Date().getTime();
    const diff = now - orderTime;
    const halfHour = 30 * 60 * 1000;
    
    if (diff >= halfHour) {
      return <span className="text-red-600 font-medium">已超时</span>;
    }
    
    const remaining = halfHour - diff;
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return <span className="text-yellow-600">{minutes}分{seconds}秒</span>;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">待处理订单</h1>
        <div className="flex items-center gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="搜索用户名..."
              value={searchUsername}
              onChange={(e) => {
                setSearchUsername(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
            />
            {searchUsername && (
              <button
                onClick={() => {
                  setSearchUsername('');
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={loadOrders}
            className="flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
          >
            <RefreshCw className="w-4 h-4" />
            刷新
          </button>
        </div>
      </div>

      {/* 数据表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">类型</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">用户名</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">金额</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">TXID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">状态</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">剩余时间</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">创建时间</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-12">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin text-pink-500" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-gray-500">暂无待处理订单</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={`${order.id}-${order.order_type || order.type || 'unknown'}`} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">
                      <div className="flex items-center gap-2">
                        {getOrderTypeIcon(order)}
                        <span>{getOrderTypeName(order)}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">{order.id}</td>
                    <td className="py-3 px-4 text-sm">
                      {(isExpansionOrder(order) || isDonationOrder(order) || isRoomMembershipOrder(order) || isRoomPackageOrder(order) || isRoomCreationOrder(order)) ? order.username || '-' : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm font-bold text-red-600">
                      {Number(order.amount).toFixed(2)} USDT
                    </td>
                    <td className="py-3 px-4 text-sm font-mono text-xs">
                      {((order as any).txid) ? (
                        <a
                          href={`https://tronscan.org/#/transaction/${(order as any).txid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-600 hover:underline"
                        >
                          {formatAddress((order as any).txid)}
                        </a>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order)}`}>
                        {getStatusText(order)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {getTimeLeft(order)}
                    </td>
                    <td className="py-3 px-4 text-sm">{formatTime(order.created_at)}</td>
                    <td className="py-3 px-4 text-sm">
                      {!isOrderCompleted(order) && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleAction(order, 'complete')}
                            className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs"
                            title="已完成"
                          >
                            <Check className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleAction(order, 'cancel')}
                            className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                            title="取消订单"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          {isExpansionOrder(order) && (
                            <button
                              onClick={() => handleAction(order, 'upgrade')}
                              className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                              title="选择套餐扩容"
                            >
                              <ArrowLeftRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
            <div className="text-sm text-gray-600">
              共 {total} 条记录，第 {page} / {Math.ceil(total / pageSize)} 页
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                上一页
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / pageSize)}
                className="px-3 py-1 bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 操作模态框 */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">
              {actionType === 'complete' ? '完成订单' : 
               actionType === 'refund' ? '退款订单' : '选择套餐扩容'}
            </h3>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">订单ID: {selectedOrder.id}</p>
              <p className="text-sm text-gray-600">
                用户: {(selectedOrder as any).username || '未知'}
              </p>
              <p className="text-sm font-bold text-red-600">
                金额: {Number((selectedOrder as any).amount).toFixed(2)} USDT
              </p>
            </div>

            {actionType === 'complete' && (selectedOrder as any).space_added === 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  原订单未付款，请输入实际空间大小
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedSpaceAmount(500)}
                    className={`px-3 py-1 rounded ${selectedSpaceAmount === 500 ? 'bg-pink-500 text-white' : 'bg-gray-200'}`}
                  >
                    500MB
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedSpaceAmount(1024)}
                    className={`px-3 py-1 rounded ${selectedSpaceAmount === 1024 ? 'bg-pink-500 text-white' : 'bg-gray-200'}`}
                  >
                    1GB
                  </button>
                  <input
                    type="number"
                    min="1"
                    placeholder="或输入MB"
                    value={selectedSpaceAmount || ''}
                    onChange={(e) => setSelectedSpaceAmount(Number(e.target.value))}
                    className="flex-1 px-3 py-1 border rounded"
                  />
                </div>
              </div>
            )}

            {actionType === 'upgrade' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">选择套餐</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="spaceAmount"
                      value={500}
                      checked={selectedSpaceAmount === 500}
                      onChange={(e) => setSelectedSpaceAmount(Number(e.target.value))}
                      className="text-pink-500"
                    />
                    <span>500MB - 10 USDT</span>
                  </label>
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="spaceAmount"
                      value={1024}
                      checked={selectedSpaceAmount === 1024}
                      onChange={(e) => setSelectedSpaceAmount(Number(e.target.value))}
                      className="text-pink-500"
                    />
                    <span>1GB - 50 USDT</span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={submitAction}
                disabled={
                  (actionType === 'upgrade' && selectedSpaceAmount === 0) ||
                  (actionType === 'complete' && (selectedOrder as any).space_added === 0 && selectedSpaceAmount === 0)
                }
                className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                确认操作
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPendingOrders;
